import React, { type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import styles from "./HoneypotField.module.css";

export interface HoneypotFieldProps {
	/** Element id — pass a distinct one per form so two forms on a page never collide. */
	id: string;
	/** Submitted field name. The tempting default is what a form-filling bot looks for. */
	name?: string;
	onChange: (event: ChangeEvent<HTMLInputElement>) => void;
	value: string;
}

/**
 * A decoy input. It is hidden from people — moved off-screen, kept out of the tab order, and hidden
 * from the accessibility tree — but it is still a real rendered text field, so a bot that fills in
 * every input it finds fills this one too. The API silently discards any submission carrying a value.
 *
 * Deliberately not `display: none`, `visibility: hidden`, or `type="hidden"`: crawlers check for all
 * three and skip those fields, which would make the trap useless.
 */
export const HoneypotField = ({ id, name = "website", onChange, value }: HoneypotFieldProps) => {
	const { t } = useTranslation("common");

	return (
		<label aria-hidden="true" className={styles.honeypot} htmlFor={id}>
			<span>{t("honeypotLabel")}</span>
			<input autoComplete="off" id={id} name={name} onChange={onChange} tabIndex={-1} type="text" value={value} />
		</label>
	);
};
